import { supabase } from './supabaseClient';

/* ══════════════════════════════════════════════════════════════════════════
   PIACI ADATOK SZERVEROLDALI CACHE-BŐL (3a)
   ─────────────────────────────────────────────────────────────────────────
   A CoinGecko markets / global / trending adatát a market-cache Edge Function
   kérdezi le pg_cron-ból (web3kurzus-landing, migráció 20261011120000); a
   böngésző a Supabase market_cache táblájából olvas (nyilvános, csak olvasható).
   - Először csak a fetched_at-et kérdezi le (pár bájt); a payloadot csak akkor
     tölti le, ha a szerver azóta frissített.
   - `at` a valódi CoinGecko-lekérdezés ideje; `intervalMs` a szerver ütemezése
     (ebből számol a modulfejléc „késleltetett” jelzése).
   - Ha nincs sor, hiba van, vagy az adat a várt idő háromszorosánál régebbi:
     null → a hívó a közvetlen CoinGecko-hívásra (tartalék) vált.
   ══════════════════════════════════════════════════════════════════════════ */

const last = new Map(); // key → { fetchedAt, data, intervalMs }

/* A szerver legutóbbi sikertelen lekérdezésének oka (market_cache.last_error,
   pl. „HTTP 401 · 10010 · Invalid API key”) → magyar szöveg a modulfejlécbe. */
export function cgReason(lastError) {
  const e = String(lastError || '');
  if (!e) return null;
  const code = (e.match(/\b(1000\d|1001\d)\b/) || [])[1];
  if (code === '10010' || code === '10011') return `CoinGecko: a kulcs és a csomagbeállítás (demo/basic) nem egyezik (${code})`;
  if (code === '10005') return 'CoinGecko: a csomag nem engedi ezt az adatot (10005)';
  if (/HTTP 401|\b10002\b/.test(e)) return 'CoinGecko: érvénytelen vagy hiányzó API-kulcs (401)';
  if (/HTTP 429|\b429\b/.test(e)) return 'CoinGecko: elfogyott a keret vagy túl sok a kérés (429)';
  const st = (e.match(/HTTP (\d{3})/) || [])[1];
  return `A CoinGecko most nem elérhető${st && st !== '0' ? ` (${st})` : ''}`;
}

export async function readMarketCache(key) {
  try {
    const { data: head, error } = await supabase
      .from('market_cache').select('fetched_at, interval_seconds, last_error, last_error_at').eq('key', key).maybeSingle();
    if (error || !head) return null;
    const at = Date.parse(head.fetched_at);
    const intervalMs = (head.interval_seconds || 300) * 1000;
    if (!Number.isFinite(at) || Date.now() - at > 3 * intervalMs) return null;
    // a legutóbbi szerveroldali próbálkozás hibás volt → a modul hibát jelez, az utolsó jó adat marad
    const errAt = head.last_error_at ? Date.parse(head.last_error_at) : 0;
    const cgError = head.last_error && errAt > at ? cgReason(head.last_error) : null;
    const prev = last.get(key);
    if (prev && prev.fetchedAt === head.fetched_at) return { data: prev.data, at, intervalMs, changed: false, error: cgError };
    const { data: row, error: e2 } = await supabase
      .from('market_cache').select('payload, fetched_at').eq('key', key).maybeSingle();
    if (e2 || !row?.payload) return null;
    last.set(key, { fetchedAt: row.fetched_at, data: row.payload, intervalMs });
    return { data: row.payload, at: Date.parse(row.fetched_at), intervalMs, changed: true, error: cgError };
  } catch (e) {
    console.warn('[marketCache]', key, e?.message || e);
    return null;
  }
}
